var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { rechartsDiagonalStripeSwatchCss } from '@web/components/layout/RechartsItems/rechartsDiagonalStripes';
import { WORK_LOCATION_BREAKDOWN_CONFIG } from '@web/config';
import { classNames } from '@web/utility';
import { ComparisonPeriod, PeriodComparisonSectionHeader, resolvePeriodComparisonSectionLabels } from '@web/utility/ tooltip/periodComparisonTooltip';
import { TOOLTIP_PANEL_RECHARTS_CLASS, TOOLTIP_SERIES_SEPARATOR_CLASS } from '@web/utility/ tooltip/tremorTooltip';
import { formatDuration } from '@workalizer/shared/utils';
var WorkLocation;
(function (WorkLocation) {
    WorkLocation["Office"] = "officeWork";
    WorkLocation["Remote"] = "remoteWork";
})(WorkLocation || (WorkLocation = {}));
var WORK_LOCATION_TOOLTIP_SECTIONS = [
    {
        section: ComparisonPeriod.Previous,
        rows: [
            { dataKey: 'previousOffice', location: WorkLocation.Office },
            { dataKey: 'previousRemote', location: WorkLocation.Remote }
        ]
    },
    {
        section: ComparisonPeriod.Current,
        rows: [
            { dataKey: 'currentOffice', location: WorkLocation.Office },
            { dataKey: 'currentRemote', location: WorkLocation.Remote }
        ]
    },
    {
        section: ComparisonPeriod.Team,
        rows: [
            { dataKey: 'teamOffice', location: WorkLocation.Office },
            { dataKey: 'teamRemote', location: WorkLocation.Remote }
        ]
    }
];
var getWorkLocationTooltipBarColor = function (dataKey) {
    switch (dataKey) {
        case 'previousOffice':
            return WORK_LOCATION_BREAKDOWN_CONFIG.office.previousPeriodColor;
        case 'previousRemote':
            return WORK_LOCATION_BREAKDOWN_CONFIG.remote.previousPeriodColor;
        case 'currentOffice':
            return WORK_LOCATION_BREAKDOWN_CONFIG.office.currentPeriodColor;
        case 'currentRemote':
            return WORK_LOCATION_BREAKDOWN_CONFIG.remote.currentPeriodColor;
        case 'teamOffice':
            return WORK_LOCATION_BREAKDOWN_CONFIG.office.teamAverageColor;
        case 'teamRemote':
            return WORK_LOCATION_BREAKDOWN_CONFIG.remote.teamAverageColor;
        default:
            return '#6b7280';
    }
};
var getWorkLocationValue = function (dataKey, periodData) {
    var _a, _b, _c;
    var valueMap = {
        previousOffice: periodData.previousOfficeHours,
        previousRemote: periodData.previousRemoteHours,
        currentOffice: periodData.officeHours,
        currentRemote: periodData.remoteHours,
        teamOffice: (_a = periodData.teamAverageOfficeHours) !== null && _a !== void 0 ? _a : 0,
        teamRemote: (_b = periodData.teamAverageRemoteHours) !== null && _b !== void 0 ? _b : 0
    };
    return (_c = valueMap[dataKey]) !== null && _c !== void 0 ? _c : 0;
};
export function WorkLocationBreakdownTooltip(_a) {
    var active = _a.active, label = _a.label, t = _a.t, showTeam = _a.showTeam, periodData = _a.periodData, dateFrom = _a.dateFrom, dateTo = _a.dateTo;
    if (!active || !periodData)
        return null;
    var metricLabel = function (location) {
        var key = location === WorkLocation.Office ? 'office' : 'remote';
        return t("tooltip.metrics.".concat(key));
    };
    var _b = resolvePeriodComparisonSectionLabels(String(label !== null && label !== void 0 ? label : ''), periodData, dateFrom, dateTo), currentPeriodLabel = _b.current, previousPeriodLabel = _b.previous;
    var sectionPeriodLabels = {
        previous: previousPeriodLabel,
        current: currentPeriodLabel,
        team: undefined
    };
    var sections = WORK_LOCATION_TOOLTIP_SECTIONS.filter(function (section) { return section.section !== ComparisonPeriod.Team || showTeam; });
    var allValues = sections.flatMap(function (section) {
        return section.rows.map(function (row) { return getWorkLocationValue(row.dataKey, periodData); });
    });
    if (!allValues.length || allValues.every(function (v) { return v === 0; }))
        return null;
    return (_jsx("div", __assign({ className: classNames(TOOLTIP_PANEL_RECHARTS_CLASS, 'min-w-[200px]') }, { children: _jsx("div", __assign({ className: "flex flex-col gap-3" }, { children: sections.map(function (section, sectionIndex) {
                var sectionRows = section.rows.map(function (row) { return (__assign(__assign({}, row), { value: getWorkLocationValue(row.dataKey, periodData), color: getWorkLocationTooltipBarColor(row.dataKey) })); });
                return (_jsxs("div", { children: [sectionIndex > 0 && (_jsx("div", { className: classNames(TOOLTIP_SERIES_SEPARATOR_CLASS, 'mb-3'), role: "separator" })), _jsx(PeriodComparisonSectionHeader, { section: section.section, periodLabel: sectionPeriodLabels[section.section], t: t }), _jsx("div", __assign({ className: "mt-1.5 flex flex-col gap-2" }, { children: sectionRows.map(function (row) {
                                var isTeamStripeSwatch = showTeam &&
                                    (row.dataKey === 'teamOffice' ||
                                        row.dataKey === 'teamRemote');
                                return (_jsxs("div", __assign({ className: "flex items-center justify-between gap-2" }, { children: [_jsxs("div", __assign({ className: "flex items-center gap-2" }, { children: [isTeamStripeSwatch ? (_jsx("div", { className: "h-2 w-2 shrink-0 rounded-full border border-gray-200", style: {
                                                        background: rechartsDiagonalStripeSwatchCss(row.color)
                                                    }, "aria-hidden": true })) : (_jsx("div", { className: "h-2 w-2 shrink-0 rounded-full", style: { backgroundColor: row.color } })), _jsx("span", __assign({ className: "text-sm text-gray-600" }, { children: metricLabel(row.location) }))] })), _jsx("span", __assign({ className: "text-sm font-semibold text-gray-900" }, { children: formatDuration(row.value, 'hours') }))] }), row.dataKey));
                            }) }))] }, section.section));
            }) })) })));
}
